import { useMemo } from "react";
import * as THREE from "three";
import { makeTextTexture, makeQrTexture } from "./textTexture";
import { tx, useTxVersion } from "../lib/tx";

/**
 * Outdoor restaurant terrace for the QUISHING scenario. Zero external assets.
 * Layout matches the scenario waypoints:
 *   - building facade + striped awning + "Restaurant Luigi" sign on the back
 *     wall (~z=-4, sign centre ~[0,2.65,-3.9]) — the opening wide shot.
 *   - a hero table front-centre at [0,0,0.6] with plates, cutlery, a wine glass
 *     and a "Scan for Menu" QR table-tent — the zoom-in target.
 *   - scattered terrace tables, planters, string lights for depth.
 */
/** Vertical sky gradient as a CanvasTexture (top deep blue -> pale horizon). */
function makeSkyTexture(): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = 4;
  c.height = 256;
  const ctx = c.getContext("2d")!;
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  g.addColorStop(0, "#4aa3da");
  g.addColorStop(0.45, "#8ec9e8");
  g.addColorStop(0.8, "#cfe9f5");
  g.addColorStop(1, "#eef6fb");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 4, 256);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** A big sky plane far behind the facade + a couple of soft clouds so the open-air
 *  terrace sits under a real sky instead of a flat white void. */
function SkyBackdrop() {
  const sky = useMemo(() => makeSkyTexture(), []);
  return (
    <group>
      <mesh position={[0, 9, -14]}>
        <planeGeometry args={[90, 40]} />
        <meshBasicMaterial map={sky} fog={false} toneMapped={false} />
      </mesh>
      {[
        [-9, 11, -13.4, 4.5],
        [7, 13, -13.6, 6],
        [0, 9.5, -13.2, 3.6],
      ].map((c, i) => (
        <mesh key={i} position={[c[0], c[1], c[2]]} scale={[c[3], c[3] * 0.42, 1]}>
          <circleGeometry args={[1, 24]} />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.85} fog={false} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

export function Restaurant({ signText, signSub }: { signText?: string; signSub?: string } = {}) {
  return (
    <group>
      <SkyBackdrop />
      <TerraceFloor />
      <Facade />
      <Awning />
      <RestaurantSign signText={signText} signSub={signSub} />
      <StringLights />

      {/* hero table (QR sticker) front-centre */}
      <Table position={[0, 0, 0.6]} hero />

      {/* background terrace tables for depth */}
      <Table position={[-2.4, 0, -0.8]} rot={0.5} />
      <Table position={[2.4, 0, -0.8]} rot={-0.4} />
      <Table position={[-2.9, 0, 1.8]} rot={0.2} />
      <Table position={[2.9, 0, 1.8]} rot={-0.2} />

      <Planter position={[-3.9, 0, -2]} />
      <Planter position={[3.9, 0, -2]} />
      <Planter position={[-4.0, 0, 1]} />
      <Planter position={[4.0, 0, 1]} />
    </group>
  );
}

function TerraceFloor() {
  // warm wooden deck with plank seams
  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[26, 26]} />
        <meshStandardMaterial color="#8a6a45" roughness={0.95} />
      </mesh>
      {Array.from({ length: 22 }).map((_, i) => (
        <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={[-10.5 + i, 0.002, 0]} receiveShadow>
          <planeGeometry args={[0.04, 26]} />
          <meshStandardMaterial color="#6f5234" roughness={1} />
        </mesh>
      ))}
    </group>
  );
}

/** Stucco building facade with door + two warmly-lit windows. */
function Facade() {
  return (
    <group position={[0, 0, -4.1]}>
      <mesh position={[0, 2.4, 0]} receiveShadow castShadow>
        <boxGeometry args={[14, 4.8, 0.3]} />
        <meshStandardMaterial color="#e7d8c3" roughness={0.95} />
      </mesh>
      {/* doorway */}
      <mesh position={[0, 1.15, 0.18]}>
        <planeGeometry args={[1.5, 2.3]} />
        <meshStandardMaterial color="#3a2c20" roughness={0.6} metalness={0.2} />
      </mesh>
      <mesh position={[0, 1.15, 0.2]}>
        <planeGeometry args={[1.2, 2.0]} />
        <meshStandardMaterial color="#26506a" roughness={0.2} metalness={0.4} transparent opacity={0.55} />
      </mesh>
      {/* windows */}
      <Window position={[-3.6, 1.75, 0.16]} />
      <Window position={[3.6, 1.75, 0.16]} />
    </group>
  );
}

/** A framed restaurant window: painted casing + stone sill, warm glowing glass
 *  behind a 2x2 mullion grid, open louvred shutters on each side, and a little
 *  flower box on the sill. Faces +z; sits on the facade. */
function Window({ position }: { position: [number, number, number] }) {
  const W = 1.7;
  const H = 1.5;
  const casing = "#f3ead7";
  const wood = "#6f4f30";
  const shutter = "#3f6b52";
  return (
    <group position={position}>
      {/* recessed reveal (dark) so the glass reads as set into the wall */}
      <mesh position={[0, 0, -0.02]}>
        <planeGeometry args={[W + 0.06, H + 0.06]} />
        <meshStandardMaterial color="#1c150e" roughness={0.9} />
      </mesh>
      {/* warm glowing glass (interior light) */}
      <mesh position={[0, 0, 0.0]}>
        <planeGeometry args={[W, H]} />
        <meshStandardMaterial
          color="#f6dca2"
          emissive={new THREE.Color("#d9a85a")}
          emissiveIntensity={0.55}
          roughness={0.25}
          metalness={0.1}
        />
      </mesh>
      {/* soft net-curtain band low in the window */}
      <mesh position={[0, -0.32, 0.012]}>
        <planeGeometry args={[W - 0.06, 0.7]} />
        <meshStandardMaterial color="#fbf3df" transparent opacity={0.35} roughness={0.9} />
      </mesh>
      {/* painted casing (four sides) */}
      {[
        { p: [0, H / 2 + 0.06, 0.03], s: [W + 0.28, 0.16, 0.1] },
        { p: [0, -H / 2 - 0.06, 0.03], s: [W + 0.28, 0.16, 0.1] },
        { p: [-W / 2 - 0.08, 0, 0.03], s: [0.14, H + 0.04, 0.1] },
        { p: [W / 2 + 0.08, 0, 0.03], s: [0.14, H + 0.04, 0.1] },
      ].map((b, i) => (
        <mesh key={i} position={b.p as [number, number, number]} castShadow>
          <boxGeometry args={b.s as [number, number, number]} />
          <meshStandardMaterial color={casing} roughness={0.85} />
        </mesh>
      ))}
      {/* stone sill */}
      <mesh position={[0, -H / 2 - 0.12, 0.08]} castShadow>
        <boxGeometry args={[W + 0.42, 0.1, 0.22]} />
        <meshStandardMaterial color="#d9cdb6" roughness={0.9} />
      </mesh>
      {/* mullions: 2 vertical + 1 horizontal -> 2x2 panes */}
      {[-W / 4, W / 4].map((x, i) => (
        <mesh key={`v${i}`} position={[x, 0, 0.02]}>
          <boxGeometry args={[0.05, H, 0.04]} />
          <meshStandardMaterial color={wood} roughness={0.7} />
        </mesh>
      ))}
      <mesh position={[0, 0, 0.02]}>
        <boxGeometry args={[W, 0.05, 0.04]} />
        <meshStandardMaterial color={wood} roughness={0.7} />
      </mesh>
      {/* open louvred shutters on each side */}
      {[-1, 1].map((s) => (
        <group key={s} position={[s * (W / 2 + 0.26), 0, 0.07]}>
          <mesh castShadow>
            <boxGeometry args={[0.3, H + 0.02, 0.04]} />
            <meshStandardMaterial color={shutter} roughness={0.8} />
          </mesh>
          {Array.from({ length: 7 }).map((_, i) => (
            <mesh key={i} position={[0, H / 2 - 0.12 - i * 0.2, 0.025]} rotation={[0.4, 0, 0]}>
              <boxGeometry args={[0.26, 0.05, 0.02]} />
              <meshStandardMaterial color="#356046" roughness={0.85} />
            </mesh>
          ))}
        </group>
      ))}
      {/* flower box on the sill */}
      <group position={[0, -H / 2 - 0.06, 0.2]}>
        <mesh castShadow>
          <boxGeometry args={[W - 0.1, 0.16, 0.16]} />
          <meshStandardMaterial color="#7a4a2c" roughness={0.9} />
        </mesh>
        {Array.from({ length: 6 }).map((_, i) => {
          const x = -W / 2 + 0.18 + i * ((W - 0.36) / 5);
          const reds = ["#d7475f", "#e7889a", "#e2b652", "#efb14a"];
          return (
            <group key={i} position={[x, 0.12, 0]}>
              <mesh>
                <icosahedronGeometry args={[0.06, 0]} />
                <meshStandardMaterial color={reds[i % reds.length]} roughness={0.8} flatShading />
              </mesh>
              <mesh position={[0.06, 0.02, 0.04]}>
                <icosahedronGeometry args={[0.045, 0]} />
                <meshStandardMaterial color="#3f7a4f" roughness={1} flatShading />
              </mesh>
            </group>
          );
        })}
      </group>
    </group>
  );
}

/** Classic red/cream striped restaurant awning over the facade. */
function Awning() {
  const stripes = 14;
  const total = 9.8;
  const w = total / stripes;
  return (
    <group position={[0, 3.55, -3.55]} rotation={[Math.PI / 2.55, 0, 0]}>
      {Array.from({ length: stripes }).map((_, i) => (
        <mesh key={i} position={[-total / 2 + w / 2 + i * w, 0, 0]} castShadow>
          <planeGeometry args={[w, 1.5]} />
          <meshStandardMaterial
            color={i % 2 === 0 ? "#b23b34" : "#f3ead7"}
            roughness={0.85}
            side={THREE.DoubleSide}
          />
        </mesh>
      ))}
      {/* scalloped front edge */}
      <mesh position={[0, -0.75, 0.02]}>
        <planeGeometry args={[total, 0.18]} />
        <meshStandardMaterial color="#9a302a" side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

/** Hanging sign board mounted on the facade. The name is per-scenario so courses
 *  can share this procedural restaurant with their own branding (defaults to
 *  "Ristorante Luigi" for the quishing course). */
function RestaurantSign({ signText, signSub }: { signText?: string; signSub?: string }) {
  useTxVersion();
  const name = signText || tx("Ristorante Luigi");
  const sub = signSub || tx("TRATTORIA  \u00B7  PIZZERIA");
  const tex = useMemo(
    () =>
      makeTextTexture(name, {
        width: 1024,
        height: 256,
        bg: "#15324a",
        radius: 28,
        color: "#f4e3b8",
        font: 'bold 120px Georgia, "Times New Roman", serif',
        sub,
        subColor: "#cdb98a",
        subFont: "44px Georgia, serif",
      }),
    [name, sub]
  );
  return (
    <group position={[0, 2.62, -3.88]}>
      {/* frame */}
      <mesh castShadow>
        <boxGeometry args={[3.5, 0.95, 0.08]} />
        <meshStandardMaterial color="#0f2233" roughness={0.6} metalness={0.3} />
      </mesh>
      <mesh position={[0, 0, 0.05]}>
        <planeGeometry args={[3.3, 0.82]} />
        <meshStandardMaterial map={tex} roughness={0.5} />
      </mesh>
      {/* little mount brackets */}
      {[-1.5, 1.5].map((x, i) => (
        <mesh key={i} position={[x, 0.6, 0]}>
          <boxGeometry args={[0.05, 0.5, 0.05]} />
          <meshStandardMaterial color="#1b1b1b" metalness={0.6} roughness={0.4} />
        </mesh>
      ))}
    </group>
  );
}

/** A run of warm string lights between two posts, overhead. */
function StringLights() {
  const bulbs = 16;
  return (
    <group position={[0, 3.0, 0.5]}>
      {Array.from({ length: bulbs }).map((_, i) => {
        const x = -5 + (i / (bulbs - 1)) * 10;
        const sag = Math.sin((i / (bulbs - 1)) * Math.PI) * 0.35;
        return (
          <mesh key={i} position={[x, -sag, 0]}>
            <sphereGeometry args={[0.05, 10, 10]} />
            <meshStandardMaterial
              color="#ffe6b0"
              emissive={new THREE.Color("#ffcf7a")}
              emissiveIntensity={1.4}
            />
          </mesh>
        );
      })}
      {/* posts */}
      {[-5, 5].map((x, i) => (
        <mesh key={i} position={[x, -1.5, 0]} castShadow>
          <cylinderGeometry args={[0.05, 0.06, 3, 10]} />
          <meshStandardMaterial color="#3a3a3a" metalness={0.4} roughness={0.6} />
        </mesh>
      ))}
    </group>
  );
}

/** A round bistro table with chairs + place settings. The hero table also gets a
 *  "Scan for Menu" QR table-tent. */
function Table({ position, rot = 0, hero = false }: { position: [number, number, number]; rot?: number; hero?: boolean }) {
  const cloth = hero ? "#f7f3ec" : "#eae3d6";
  return (
    <group position={position} rotation={[0, rot, 0]}>
      {/* pedestal */}
      <mesh position={[0, 0.36, 0]} castShadow>
        <cylinderGeometry args={[0.05, 0.09, 0.72, 12]} />
        <meshStandardMaterial color="#2b2b2b" metalness={0.5} roughness={0.4} />
      </mesh>
      <mesh position={[0, 0.02, 0]} receiveShadow>
        <cylinderGeometry args={[0.32, 0.36, 0.04, 16]} />
        <meshStandardMaterial color="#1f1f1f" metalness={0.4} roughness={0.5} />
      </mesh>
      {/* tablecloth top */}
      <mesh position={[0, 0.74, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.62, 0.62, 0.05, 32]} />
        <meshStandardMaterial color={cloth} roughness={0.95} />
      </mesh>
      {/* floor-length draped tablecloth (was a short skirt that floated mid-air
          and read as a white "tub"; now it hangs to the deck like real cloth) */}
      <mesh position={[0, 0.37, 0]} castShadow>
        <cylinderGeometry args={[0.6, 0.66, 0.72, 36, 1, true]} />
        <meshStandardMaterial color={cloth} roughness={1} side={THREE.DoubleSide} />
      </mesh>
      {/* soft scalloped hem at the bottom */}
      <mesh position={[0, 0.02, 0]}>
        <cylinderGeometry args={[0.66, 0.66, 0.04, 36, 1, true]} />
        <meshStandardMaterial color={cloth} roughness={1} side={THREE.DoubleSide} />
      </mesh>

      {/* place settings */}
      <PlaceSetting angle={0} />
      <PlaceSetting angle={Math.PI} />
      {hero && <PlaceSetting angle={Math.PI / 2} />}

      {/* chairs */}
      <Chair angle={0} />
      <Chair angle={Math.PI} />

      {hero && <QrTent />}
      {hero && <CenterPiece />}
    </group>
  );
}

function PlaceSetting({ angle }: { angle: number }) {
  const r = 0.34;
  const x = Math.sin(angle) * r;
  const z = Math.cos(angle) * r;
  return (
    <group position={[x, 0.772, z]} rotation={[0, -angle, 0]}>
      {/* plate */}
      <mesh receiveShadow castShadow>
        <cylinderGeometry args={[0.12, 0.115, 0.012, 28]} />
        <meshStandardMaterial color="#fbfbfd" roughness={0.35} metalness={0.05} />
      </mesh>
      <mesh position={[0, 0.008, 0]}>
        <cylinderGeometry args={[0.075, 0.075, 0.004, 24]} />
        <meshStandardMaterial color="#eef0f4" roughness={0.4} />
      </mesh>
      {/* fork + knife */}
      <mesh position={[-0.17, -0.002, 0]}>
        <boxGeometry args={[0.012, 0.004, 0.13]} />
        <meshStandardMaterial color="#cfd3d8" metalness={0.85} roughness={0.25} />
      </mesh>
      <mesh position={[0.17, -0.002, 0]}>
        <boxGeometry args={[0.012, 0.004, 0.13]} />
        <meshStandardMaterial color="#cfd3d8" metalness={0.85} roughness={0.25} />
      </mesh>
      {/* wine glass */}
      <group position={[0.13, 0.05, -0.12]}>
        <mesh position={[0, 0.06, 0]}>
          <cylinderGeometry args={[0.035, 0.02, 0.09, 16, 1, true]} />
          <meshStandardMaterial color="#cfe3f0" roughness={0.05} metalness={0.1} transparent opacity={0.45} side={THREE.DoubleSide} />
        </mesh>
        <mesh position={[0, 0.01, 0]}>
          <cylinderGeometry args={[0.004, 0.004, 0.08, 8]} />
          <meshStandardMaterial color="#cfe3f0" transparent opacity={0.5} />
        </mesh>
        <mesh position={[0, -0.028, 0]}>
          <cylinderGeometry args={[0.03, 0.03, 0.005, 16]} />
          <meshStandardMaterial color="#cfe3f0" transparent opacity={0.5} />
        </mesh>
      </group>
    </group>
  );
}

function CenterPiece() {
  return (
    <group position={[0, 0.78, 0]}>
      {/* small vase */}
      <mesh position={[0, 0.05, 0]} castShadow>
        <cylinderGeometry args={[0.03, 0.04, 0.1, 12]} />
        <meshStandardMaterial color="#9fb7c9" roughness={0.3} metalness={0.2} />
      </mesh>
      {[0, 1, 2].map((i) => (
        <mesh key={i} position={[(i - 1) * 0.02, 0.16, 0]} rotation={[0, 0, (i - 1) * 0.3]}>
          <sphereGeometry args={[0.025, 8, 8]} />
          <meshStandardMaterial color={i === 1 ? "#d7475f" : "#e7889a"} roughness={0.8} flatShading />
        </mesh>
      ))}
    </group>
  );
}

/** The QR "Scan for Menu" table tent the learner scans. A clean acrylic A-frame:
 *  the QR is printed on the FRONT panel (facing the camera, +z); a plain back
 *  panel leans the other way so the two meet at a ridge. No stray plane in front
 *  of the QR (the old grey "bar" was a back support that tilted across it). */
function QrTent() {
  useTxVersion();
  const caption = tx("Scan for Menu");
  const qr = useMemo(() => makeQrTexture(caption), [caption]);
  const tilt = 0.22; // lean from vertical
  const h = 0.26;
  return (
    <group position={[0.0, 0.772, 0.16]}>
      {/* front panel: QR, bottom forward, top toward the ridge (leans back) */}
      <mesh position={[0, h / 2, 0.04]} rotation={[tilt, 0, 0]} castShadow>
        <planeGeometry args={[0.22, h]} />
        <meshStandardMaterial
          map={qr}
          roughness={0.55}
          side={THREE.DoubleSide}
          polygonOffset
          polygonOffsetFactor={-2}
          polygonOffsetUnits={-2}
        />
      </mesh>
      {/* back panel: plain card leaning the opposite way to form the /\ tent */}
      <mesh position={[0, h / 2, -0.04]} rotation={[-tilt, 0, 0]} castShadow>
        <planeGeometry args={[0.22, h]} />
        <meshStandardMaterial color="#f2efe9" roughness={0.85} side={THREE.DoubleSide} />
      </mesh>
      {/* small weighted base/foot under the tent */}
      <mesh position={[0, 0.012, 0]} castShadow>
        <boxGeometry args={[0.2, 0.024, 0.12]} />
        <meshStandardMaterial color="#cfc9bd" roughness={0.7} metalness={0.1} />
      </mesh>
    </group>
  );
}

function Chair({ angle }: { angle: number }) {
  const r = 0.78;
  const x = Math.sin(angle) * r;
  const z = Math.cos(angle) * r;
  return (
    <group position={[x, 0, z]} rotation={[0, -angle + Math.PI, 0]}>
      <mesh position={[0, 0.45, 0]} castShadow>
        <boxGeometry args={[0.42, 0.04, 0.42]} />
        <meshStandardMaterial color="#5a3c25" roughness={0.8} />
      </mesh>
      <mesh position={[0, 0.72, 0.2]} castShadow>
        <boxGeometry args={[0.42, 0.5, 0.04]} />
        <meshStandardMaterial color="#5a3c25" roughness={0.8} />
      </mesh>
      {[
        [-0.18, 0.18],
        [0.18, 0.18],
        [-0.18, -0.18],
        [0.18, -0.18],
      ].map((p, i) => (
        <mesh key={i} position={[p[0], 0.22, p[1]]} castShadow>
          <boxGeometry args={[0.04, 0.44, 0.04]} />
          <meshStandardMaterial color="#3f2a19" roughness={0.8} />
        </mesh>
      ))}
    </group>
  );
}

function Planter({ position }: { position: [number, number, number] }) {
  return (
    <group position={position}>
      <mesh position={[0, 0.25, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.6, 0.5, 0.6]} />
        <meshStandardMaterial color="#6b6f76" roughness={0.95} />
      </mesh>
      <mesh position={[0, 0.78, 0]} castShadow>
        <icosahedronGeometry args={[0.42, 1]} />
        <meshStandardMaterial color="#3f7a4f" roughness={1} flatShading />
      </mesh>
      <mesh position={[0.2, 0.98, 0.06]} castShadow>
        <icosahedronGeometry args={[0.28, 1]} />
        <meshStandardMaterial color="#46885a" roughness={1} flatShading />
      </mesh>
      <mesh position={[-0.18, 1.04, -0.05]} castShadow>
        <icosahedronGeometry args={[0.24, 1]} />
        <meshStandardMaterial color="#356b44" roughness={1} flatShading />
      </mesh>
    </group>
  );
}
